import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { BasketProvider } from './contexts/BasketContext'
import { CurrencyProvider } from './contexts/CurrencyContext'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
import PublicLayout from './components/PublicLayout'
import AuthCallback from './pages/AuthCallback'
import { FeedbackProvider } from './components/ui/Feedback'
import { LoadingState } from './components/ui/States'
import StaffNotFound from './pages/StaffNotFound'
import ErrorBoundary from './components/ErrorBoundary'
import { playerFilePath } from './utils/playerPath'

// Public marketing site
const PublicHome = lazy(() => import('./pages/public/Home'))
const PublicRules = lazy(() => import('./pages/public/Rules'))
const PublicGangRules = lazy(() => import('./pages/public/GangRules'))
const PublicStore = lazy(() => import('./pages/public/StorePage'))
const SignIn = lazy(() => import('./pages/SignIn'))
const LegalPage = lazy(() => import('./pages/public/LegalPage'))

// Staff dashboard — mounted under /staff/*
const Home = lazy(() => import('./pages/Home'))
const StaffRoster = lazy(() => import('./pages/StaffRoster'))
const StaffLOA = lazy(() => import('./pages/StaffLOA'))
const Reports = lazy(() => import('./pages/Reports'))
const GangRoster = lazy(() => import('./pages/GangRoster'))
const StaffVote = lazy(() => import('./pages/StaffVote'))
const PremiumDrugLogs = lazy(() => import('./pages/PremiumDrugLogs'))
const MediaAssets = lazy(() => import('./pages/MediaAssets'))
const Documentation = lazy(() => import('./pages/Documentation'))
const Realtor = lazy(() => import('./pages/Realtor'))
const ImportsLog = lazy(() => import('./pages/ImportsLog'))
const RadioLocks = lazy(() => import('./pages/RadioLocks'))
const BansManager = lazy(() => import('./pages/BansManager'))
const TebexTransactions = lazy(() => import('./pages/TebexTransactions'))
const AwardsPage = lazy(() => import('./pages/awards/AwardsPage'))
const AwardsAdmin = lazy(() => import('./pages/awards/AwardsAdmin'))
const ContentEditor = lazy(() => import('./pages/ContentEditor'))
const PlayerLookup = lazy(() => import('./pages/PlayerLookup'))
const PlayerReimbursement = lazy(() => import('./pages/PlayerReimbursement'))
const PunishmentHistory = lazy(() => import('./pages/PunishmentHistory'))
const ActivityLog = lazy(() => import('./pages/ActivityLog'))
const StaffHours = lazy(() => import('./pages/StaffHours'))
const GangCooldown = lazy(() => import('./pages/GangCooldown'))
const Apartments = lazy(() => import('./pages/Apartments'))
const BusinessHours = lazy(() => import('./pages/BusinessHours'))
const BusinessHoursDetail = lazy(() => import('./pages/BusinessHoursDetail'))
const ResourceSwiper = lazy(() => import('./pages/ResourceSwiper'))

function LegacyPlayerRedirect() {
  const { key } = useParams()
  const to = playerFilePath({ discord: key, license: key, identifier: key }) || '/staff/player-lookup'
  return <Navigate to={to} replace />
}

function App() {
  return (
    <AuthProvider>
      <FeedbackProvider>
      <ErrorBoundary>
      <Router>
        <CurrencyProvider>
        <BasketProvider>
          <Suspense fallback={null}>
          <Routes>
            {/* OAuth callback at root — Discord redirect URI matches /auth/callback */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Public marketing site — wrapped in PublicLayout (top nav + Staff Login button) */}
            <Route
              path="/"
              element={
                <PublicLayout>
                  <PublicHome />
                </PublicLayout>
              }
            />
            <Route
              path="/rules"
              element={
                <PublicLayout>
                  <PublicRules />
                </PublicLayout>
              }
            />
            <Route
              path="/gang-rules"
              element={
                <PublicLayout>
                  <PublicGangRules />
                </PublicLayout>
              }
            />
            {/* In-platform Tebex storefront (Headless API + Tebex.js checkout) */}
            <Route
              path="/store"
              element={
                <PublicLayout>
                  <PublicStore />
                </PublicLayout>
              }
            />

            {/* Staff dashboard — protected, with sidebar Layout. Inner routes are relative. */}
            <Route
              path="/staff/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Suspense fallback={<LoadingState />}>
                      <Routes>
                        <Route index element={<Home />} />
                        <Route path="awards" element={<AwardsPage />} />
                        <Route path="awards/admin" element={<AwardsAdmin />} />
                        <Route path="staff-roster" element={<StaffRoster />} />
                        <Route path="staff-loa" element={<StaffLOA />} />
                        <Route path="reports" element={<Reports />} />
                        <Route path="staff-vote" element={<StaffVote />} />
                        <Route path="gang-roster" element={<GangRoster />} />
                        <Route path="gang-cooldown" element={<GangCooldown />} />
                        <Route path="apartments" element={<Apartments />} />
                        <Route path="resource-swiper" element={<ResourceSwiper />} />
                        <Route path="premium-drug-logs" element={<PremiumDrugLogs />} />
                        <Route path="assets" element={<MediaAssets />} />
                        <Route path="documentation" element={<Documentation />} />
                        <Route path="realtor" element={<Realtor />} />
                        <Route path="imports-log" element={<ImportsLog />} />
                        <Route path="radio-locks" element={<RadioLocks />} />
                        <Route path="bans-manager" element={<BansManager />} />
                        <Route path="tebex-transactions" element={<TebexTransactions />} />
                        <Route path="content" element={<ContentEditor />} />
                        <Route path="player-lookup" element={<PlayerLookup />} />
                        <Route path="player-reimbursement" element={<PlayerReimbursement />} />
                        <Route path="punishment-history" element={<PunishmentHistory />} />
                        <Route path="players" element={<Navigate to="/staff/player-lookup" replace />} />
                        <Route path="players/:key" element={<LegacyPlayerRedirect />} />
                        <Route path="activity" element={<ActivityLog />} />
                        <Route path="staff-hours" element={<StaffHours />} />
                        <Route path="business-hours" element={<BusinessHours />} />
                        <Route path="business-hours/:job" element={<BusinessHoursDetail />} />
                        <Route path="*" element={<StaffNotFound />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Refund + Privacy policies (content in src/content/legal.js — not dashboard-editable).
                Tebex Terms is an external link in the footer; our own ToS page is not routed. */}
            <Route path="/refund" element={<PublicLayout><LegalPage /></PublicLayout>} />
            <Route path="/privacy" element={<PublicLayout><LegalPage /></PublicLayout>} />

            {/* Sign-in landing (store checkout + Discord). Standalone — no layout. */}
            <Route path="/signin" element={<SignIn />} />

            {/* Legacy /login → sign-in page. */}
            <Route path="/login" element={<Navigate to="/signin" replace />} />
          </Routes>
          </Suspense>
        </BasketProvider>
        </CurrencyProvider>
      </Router>
      </ErrorBoundary>
      </FeedbackProvider>
    </AuthProvider>
  )
}

export default App
